import { useEffect, useState, useCallback } from 'react';
import { 
  Upload, 
  Download, 
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  AlertTriangle
} from 'lucide-react';
import { uploadCSV, getImportHistory, downloadTemplate } from '../services/api';
import { ImportLog } from '../types';
import clsx from 'clsx';
import { formatDate } from '../utils/dates';

export default function Import() {
  const [imports, setImports] = useState<ImportLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadResult, setUploadResult] = useState<ImportLog | null>(null);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const response = await getImportHistory();
      if (response.success && response.data) {
        setImports(response.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const handleFile = async (file: File) => {
    if (!file.name.endsWith('.csv')) {
      alert('Please upload a CSV file');
      return;
    }

    try {
      setUploading(true);
      setUploadResult(null);
      const response = await uploadCSV(file);
      if (response.success && response.data) {
        setUploadResult(response.data);
        fetchHistory();
      } else {
        alert('Upload failed: ' + (response.error || 'Unknown error'));
      }
    } catch (err: any) {
      alert('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await downloadTemplate();
    } catch (err: any) {
      alert('Failed to download template: ' + err.message);
    }
  };

  const statusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-success-600" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-danger-600" />;
      case 'processing':
        return <Clock className="w-5 h-5 text-warning-600 animate-pulse" />;
      default:
        return <FileText className="w-5 h-5 text-gray-400" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Import Challans</h2>
          <p className="text-sm text-gray-500 mt-1">Upload CSV files to bulk import challan data</p>
        </div>
        <button onClick={handleDownloadTemplate} className="btn-secondary">
          <Download className="w-4 h-4 mr-2" />
          Download Template
        </button>
      </div>

      {/* Upload Area */}
      <div className="card p-6">
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={clsx(
            'border-2 border-dashed rounded-xl p-12 text-center transition-colors',
            dragActive ? 'border-primary-500 bg-primary-50' : 'border-gray-300',
            uploading ? 'opacity-50 pointer-events-none' : ''
          )}
        >
          <input
            type="file"
            accept=".csv"
            onChange={handleChange}
            className="hidden"
            id="csv-upload"
            disabled={uploading}
          />
          <label htmlFor="csv-upload" className="cursor-pointer">
            {uploading ? (
              <RefreshCw className="w-12 h-12 mx-auto text-primary-600 animate-spin mb-4" />
            ) : (
              <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            )}
            <p className="text-lg font-medium text-gray-900 mb-2">
              {uploading ? 'Uploading...' : 'Drop CSV file here or click to browse'}
            </p>
            <p className="text-sm text-gray-500">
              Supports .csv files up to 10MB
            </p>
          </label>
        </div>

        {/* Upload Result */}
        {uploadResult && (
          <div className={clsx(
            'mt-4 p-4 rounded-lg',
            uploadResult.status === 'completed' ? 'bg-success-50' : 
            uploadResult.status === 'failed' ? 'bg-danger-50' : 'bg-warning-50'
          )}>
            <div className="flex items-start gap-3">
              {statusIcon(uploadResult.status)}
              <div className="flex-1">
                <p className="font-medium text-gray-900">
                  {uploadResult.status === 'completed' ? 'Import Completed' : 
                   uploadResult.status === 'failed' ? 'Import Failed' : 'Processing...'}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  {uploadResult.fileName}
                </p>
                {uploadResult.stats && (
                  <div className="flex flex-wrap gap-4 mt-2 text-sm">
                    <span className="text-gray-600">
                      Total: <strong>{uploadResult.stats.totalRows}</strong>
                    </span>
                    <span className="text-success-600">
                      Success: <strong>{uploadResult.stats.successRows}</strong>
                    </span>
                    <span className="text-danger-600">
                      Failed: <strong>{uploadResult.stats.failedRows}</strong>
                    </span>
                    <span className="text-primary-600">
                      New: <strong>{uploadResult.stats.newChallans}</strong>
                    </span>
                    <span className="text-gray-600">
                      Updated: <strong>{uploadResult.stats.updatedChallans}</strong>
                    </span>
                  </div>
                )}
                {uploadResult.errorMessage && (
                  <p className="text-sm text-danger-600 mt-2">{uploadResult.errorMessage}</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* CSV Format Info */}
      <div className="card p-6">
        <h3 className="text-sm font-medium text-gray-900 mb-3">CSV Format Requirements</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="font-medium text-gray-700 mb-2">Required Fields:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-1">
              <li>vehicle_number (e.g., MH12AB1234)</li>
              <li>challan_number (unique ID)</li>
              <li>challan_date (YYYY-MM-DD)</li>
              <li>challan_status (PENDING/PAID)</li>
              <li>challan_amount (number)</li>
            </ul>
          </div>
          <div>
            <p className="font-medium text-gray-700 mb-2">Optional Fields:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-1">
              <li>state_code, district, challan_place</li>
              <li>offence_codes (pipe-separated)</li>
              <li>offence_descriptions (pipe-separated)</li>
              <li>payment_date, payment_amount, payment_mode</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Import History */}
      <div className="card">
        <div className="p-5 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Import History</h3>
        </div>
        
        {loading ? (
          <div className="p-12 text-center">
            <RefreshCw className="w-8 h-8 mx-auto text-primary-600 animate-spin" />
          </div>
        ) : imports.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No imports yet</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {imports.map((imp) => (
              <div key={imp.importId} className="p-4 flex items-center gap-4">
                {statusIcon(imp.status)}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{imp.fileName}</p>
                  <p className="text-sm text-gray-500">
                    {formatDate(imp.startedAt, 'datetime')}
                  </p>
                </div>
                <div className="text-right text-sm">
                  {imp.stats && (
                    <>
                      <p className="text-gray-900">
                        {imp.stats.successRows}/{imp.stats.totalRows} rows
                      </p>
                      <p className="text-gray-500">
                        {imp.stats.newChallans} new, {imp.stats.updatedChallans} updated
                      </p>
                    </>
                  )}
                  {imp.status === 'failed' && (
                    <p className="text-danger-600">Failed</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
